import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { photos } from '../../data/site';
import { repertoire } from '../../data/repertoire';
import { ButtonLink } from '../ui/ButtonLink';
import { DottedRing } from '../ui/DottedRing';
const ease = [0.23, 1, 0.32, 1] as const;
const songCount = Math.floor(repertoire.reduce((n, e) => n + e.songs.length, 0) / 10) * 10;
export function Hero() {
  const reduce = useReducedMotion();
  const fade = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease
    }
  });
  return <section className="relative w-full overflow-hidden bg-ink pt-16 md:pt-20" data-id="element-68" data-mp="r5hkvrpco58">
      <p aria-hidden className="pointer-events-none absolute bottom-24 left-3 hidden origin-bottom-left -rotate-90 whitespace-nowrap text-[10px] uppercase tracking-[0.3em] text-muted xl:block" data-id="element-69" data-mp="3rxgkkxg9vyz7">
        Huizen, Nederland — een muzikaal verhaal
      </p>

      <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-8 md:px-10 lg:pb-28 lg:pt-6" data-id="element-70" data-mp="1mwm24026hs6h">
        <motion.p {...fade(0)} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-amber" data-id="element-71" data-mp="1rv5qs885pi2j">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber" data-id="element-72" data-mp="1cz7zp4d3b3x6" />
          Singer-songwriter · Piano- &amp; gitaardocent · Huizen
        </motion.p>

        <motion.p {...fade(0.05)} aria-hidden className="relative z-10 mt-6 font-serif text-[15vw] leading-[0.9] tracking-[-0.02em] text-paper lg:whitespace-nowrap lg:text-[min(10.5vw,136px)]" data-id="element-73" data-mp="25mu0y5yuhwgv">
          Bas <span className="italic text-amber" data-id="element-74" data-mp="1vvzf32pigyvy">Menninga</span>
        </motion.p>

        <motion.figure initial={reduce ? false : {
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.05,
        ease
      }} className="relative z-20 mt-10 w-full sm:mx-auto sm:w-2/3 lg:absolute lg:right-10 lg:top-10 lg:mt-0 lg:w-[27%]" data-id="element-75" data-mp="20vlqd6ls4524">
          <span aria-hidden className="absolute -top-2.5 left-3 h-full w-full rounded-md border border-brand" data-id="element-76" data-mp="3373oxdry967c" />
          <img src={photos.stage} alt="Bas Menninga zingt achter de microfoon onder warm podiumlicht" style={{
          objectPosition: '38% 50%'
        }} className="relative aspect-[4/5] w-full rounded object-cover shadow-[0_24px_50px_rgba(0,0,0,0.55)] lg:aspect-[342/588]" data-id="element-77" data-mp="2y9ts3d28ds9h" />
          <figcaption className="absolute -bottom-10 -left-8 rounded-full bg-brand shadow-[0_16px_32px_rgba(0,0,0,0.3)]" data-id="element-78" data-mp="2kbm0lknyrj5p">
            <DottedRing dots={32} className="w-36 text-paper/90" data-id="element-79" data-mp="2ajpqtccim4ln">
              <span className="block font-serif text-lg leading-tight text-paper" data-id="element-80" data-mp="2ajzegta37zo6">Live &amp;</span>
              <span className="block font-serif text-lg font-semibold italic leading-tight text-paper" data-id="element-81" data-mp="26jz17byq5uip">intiem</span>
            </DottedRing>
          </figcaption>
        </motion.figure>

        <div className="relative z-10 mt-16 grid grid-cols-1 gap-10 md:grid-cols-[1fr_1.3fr_1.6fr] md:gap-0 lg:mt-20 lg:w-[68%] lg:pr-6" data-id="element-82" data-mp="1wkgl56lxqsvx">
          <motion.div {...fade(0.1)} className="md:border-r md:border-line md:pr-6" data-id="element-83" data-mp="h10ig9axqfb6">
            <p className="font-serif text-sm italic text-amber" data-id="element-84" data-mp="h2tqnt1757la">01</p>
            <div className="mt-3 border-t border-line pt-5" data-id="element-85" data-mp="m0lhsku0opof">
              <p className="font-serif text-4xl italic text-amber" data-id="element-86" data-mp="q270imt8fy6j">{songCount}+</p>
              <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-87" data-mp="3pgshyhd844sg">nummers — van Hazes tot Hotel California</p>
            </div>
          </motion.div>

          <motion.div {...fade(0.15)} className="md:border-r md:border-line md:px-6" data-id="element-88" data-mp="3ugevyt0vlp2b">
            <p className="font-serif text-sm italic text-amber" data-id="element-89" data-mp="2bb3nb9x6csf">02</p>
            <h1 className="mt-3 font-serif text-5xl leading-[1.08] text-paper md:text-[2.5rem]" data-id="element-90" data-mp="73cnpjdbx7t0">
              <span className="sr-only" data-id="element-91" data-mp="36qufqhw3x5wq">Bas Menninga — </span>
              De juiste <em className="text-amber" data-id="element-92" data-mp="36ohw80apanhk">snaar</em> raken.
            </h1>
          </motion.div>

          <motion.div {...fade(0.2)} className="md:pl-6" data-id="element-93" data-mp="3apjq01lum7c5">
            <p className="font-serif text-sm italic text-amber" data-id="element-94" data-mp="2ro2rli7zdmct">03</p>
            <p className="mt-3 text-[15px] leading-relaxed text-sand" data-id="element-95" data-mp="2moq1moltbz60">
              Live muziek voor uw feest, borrel of viering — en piano- of gitaarles voor wie zelf wil leren spelen.
            </p>
            <div className="mt-6 flex flex-col gap-3" data-id="element-96" data-mp="2wm47xve0v7kl">
              <ButtonLink to="/optreden" arrow data-id="element-97" data-mp="2wkb0idvl4jbj">
                Boek een optreden
              </ButtonLink>
              <ButtonLink to="/muziekles" variant="outline" data-id="element-98" data-mp="3aflsnjig2qc7">
                Muziekles? Kijk hier
              </ButtonLink>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}